import { useState } from 'react';
import { User, Bell, Shield, CreditCard, Key, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useAuthStore } from '@/stores/authStore';
import { resetPassword } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

export function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout, updateProfile, deleteAccount } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [securityMessage, setSecurityMessage] = useState('');
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_weekly: user?.notificationPreferences?.email_weekly ?? true,
    new_posts: user?.notificationPreferences?.new_posts ?? true,
    credit_low: user?.notificationPreferences?.credit_low ?? true,
    payment: user?.notificationPreferences?.payment ?? true,
  });
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    timezone: user?.timezone || 'Europe/Rome',
  });

  const canSaveProfile = formData.fullName.trim().length > 0;

  const handleSave = async () => {
    setIsSavingProfile(true);
    try {
      await updateProfile({
        ...formData,
        notificationPreferences: notificationPrefs,
      });
      setIsEditing(false);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSaveNotifications = async () => {
    setIsSavingNotifications(true);
    try {
      await updateProfile({ notificationPreferences: notificationPrefs });
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'This will permanently delete your account and data. This action cannot be undone. Continue?'
    );
    if (!confirmed) {
      return;
    }

    setIsDeletingAccount(true);
    try {
      await deleteAccount();
      navigate('/');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!user?.email) {
      return;
    }
    setSecurityMessage('');
    setIsSendingReset(true);
    try {
      await resetPassword({ username: user.email });
      setSecurityMessage('Password reset code sent to your email.');
    } catch (_error) {
      setSecurityMessage('Unable to send password reset email right now.');
    } finally {
      setIsSendingReset(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'api', label: 'API Keys', icon: Key },
  ];

  return (
    <div className="space-y-6">
      <div className="brutalist-panel rounded-2xl p-6">
        <p className="brutalist-label">Account controls</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage profile, billing, security, and platform preferences.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="brutalist-panel-soft rounded-xl p-2 lg:w-64 space-y-2 self-start">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 border-2 px-3 py-2 text-left text-sm font-bold uppercase tracking-[0.12em] transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary bg-primary text-primary-foreground shadow-[3px_3px_0_hsl(var(--foreground)/0.16)]'
                    : 'border-transparent text-muted-foreground hover:border-border hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'profile' && (
            <Card className="brutalist-panel rounded-xl">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <>
                    <div className="space-y-2">
                      <label className="brutalist-label" htmlFor="settings-full-name">Full Name</label>
                      <Input
                        id="settings-full-name"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="brutalist-label" htmlFor="settings-email">Email</label>
                      <Input
                        id="settings-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        disabled
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="brutalist-label" htmlFor="settings-timezone">Timezone</label>
                      <Input
                        id="settings-timezone"
                        value={formData.timezone}
                        onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => void handleSave()} disabled={isSavingProfile || !canSaveProfile}>
                        {isSavingProfile ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditing(false)} disabled={isSavingProfile}>
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="brutalist-panel-soft flex items-center gap-4 rounded-xl p-4">
                      <div className="flex h-16 w-16 items-center justify-center border-2 border-border bg-primary text-primary-foreground">
                        <span className="text-2xl font-semibold">
                          {user?.fullName?.charAt(0) || user?.email.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{user?.fullName || 'No name set'}</p>
                        <p className="text-sm text-muted-foreground">{user?.email}</p>
                      </div>
                    </div>
                    <Button variant="outline" onClick={() => setIsEditing(true)}>
                      Edit Profile
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <Card className="brutalist-panel rounded-xl">
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>Choose what you want to be notified about</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[ 
                  { id: 'email_weekly', label: 'Weekly digest', description: 'Get a summary of your activity' },
                  { id: 'new_posts', label: 'New posts from targets', description: 'When someone you follow posts' },
                  { id: 'credit_low', label: 'Low credit alert', description: 'When you have less than 1000 credits' },
                  { id: 'payment', label: 'Payment notifications', description: 'Invoices and payment confirmations' },
                ].map((item) => (
                  <label key={item.id} className="brutalist-panel-soft flex items-start justify-between gap-4 rounded-xl p-4">
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                    <input
                      type="checkbox"
                      aria-label={item.label}
                      checked={notificationPrefs[item.id as keyof typeof notificationPrefs]}
                      onChange={(e) =>
                        setNotificationPrefs((prev) => ({
                          ...prev,
                          [item.id]: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border-2 border-input accent-primary"
                    />
                  </label>
                ))}
                <Button onClick={() => void handleSaveNotifications()} disabled={isSavingNotifications}>
                  {isSavingNotifications ? 'Saving...' : 'Save notification preferences'}
                </Button>
              </CardContent>
            </Card>
          )}

          {activeTab === 'security' && (
            <Card className="brutalist-panel rounded-xl">
              <CardHeader>
                <CardTitle>Security</CardTitle>
                <CardDescription>Manage your password and security settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  For security reasons, password changes are handled through email verification.
                </p>
                <Button onClick={() => void handleSendResetEmail()} disabled={isSendingReset}>
                  {isSendingReset ? 'Sending reset email...' : 'Send password reset email'}
                </Button>
                {securityMessage ? (
                  <p className="text-sm text-muted-foreground">{securityMessage}</p>
                ) : null}
              </CardContent>
            </Card>
          )}

          {activeTab === 'billing' && (
            <Card className="brutalist-panel rounded-xl">
              <CardHeader>
                <CardTitle>Billing</CardTitle>
                <CardDescription>Manage your subscription and payment methods</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="brutalist-panel-soft flex items-center justify-between gap-4 rounded-xl p-4">
                  <div>
                    <p className="font-medium capitalize">{user?.plan} Plan</p>
                    <p className="text-sm text-muted-foreground">
                      {user?.creditsBalance.toLocaleString()} credits remaining
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigate('/plans')}>
                    Upgrade
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <Button variant="outline" onClick={() => navigate('/plans')}>
                  Open billing and invoices
                </Button>
              </CardContent>
            </Card>
          )}

          {activeTab === 'api' && (
            <Card className="brutalist-panel rounded-xl">
              <CardHeader>
                <CardTitle>API Keys</CardTitle>
                <CardDescription>Manage your API access keys</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="brutalist-panel-soft rounded-xl p-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-medium">API Access</p>
                      <p className="text-sm text-muted-foreground font-mono">
                        API key self-service is not enabled in this release.
                      </p>
                    </div>
                    <Button variant="outline" size="sm" disabled>Reveal</Button>
                  </div>
                </div>
                <Button variant="outline" disabled>Generate New Key</Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Logout */}
      <Card className="brutalist-panel rounded-xl border-destructive">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium text-destructive">Danger Zone</p>
              <p className="text-sm text-muted-foreground">
                Log out or delete your account permanently
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={logout}>
                Log Out
              </Button>
              <Button variant="destructive" disabled={isDeletingAccount} onClick={() => void handleDeleteAccount()}>
                Delete Account
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
